import { useRef } from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import {
  EmployeeBlocksEmployeeTaxResidenceFragment,
  EmployeeBlocksEmployeeTaxResidenceFragmentDoc,
  EmployeeBlocksUpsertTaxResidenceExtraInformationDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import AddressInput, {
  ADDRESS_ROW_TYPES,
  isScrollAddressRowType,
} from 'modules/core/components/AddressInput'
import FormComponent from 'modules/core/components/Form'
import FormRow from 'modules/core/components/FormRow'
import InlineTooltip from 'modules/core/components/InlineTooltip'
import DayPicker from 'modules/core/components/Inputs/DayPicker'
import Text from 'modules/core/components/Inputs/Text'
import { useMutation } from 'modules/core/hooks/useMutation'
import { stringToDate } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import type { ScrollRowType } from 'modules/employees/components/blocks/TaxResidence/types'
import ExtraInformationForm from 'modules/employees/lib/forms/Personal/ExtraInformation'

interface Props {
  employeeId: number
  employeeTaxResidence?: EmployeeBlocksEmployeeTaxResidenceFragment | null
  readonly: boolean
  scrollTo?: ScrollRowType | null | undefined
}

const ExtraInformation = ({
  employeeId,
  employeeTaxResidence,
  scrollTo = null,
  readonly,
}: Props) => {
  const form = useRef(
    new ExtraInformationForm({
      employee_id: employeeId,
      address_line_1: employeeTaxResidence?.addressLine1 ?? '',
      address_line_2: employeeTaxResidence?.addressLine2 ?? '',
      city: employeeTaxResidence?.city ?? '',
      country: employeeTaxResidence?.country ?? '',
      identifier: employeeTaxResidence?.identifier ?? '',
      no_resident_end_date: employeeTaxResidence?.noResidentEndDate ?? '',
      postal_code: employeeTaxResidence?.postalCode ?? '',
      state: employeeTaxResidence?.state ?? '',
    })
  )
  const extraInformationForm = form.current

  const [upsertTaxResidence] = useMutation(
    EmployeeBlocksUpsertTaxResidenceExtraInformationDocument,
    {
      extractorFn: (data) => data.hiringVersionsMutations.upsertHiringversionsTaxResidence,
      update: (cache, result) => {
        const updatedTaxResidence =
          result.data?.hiringVersionsMutations?.upsertHiringversionsTaxResidence?.taxResidence
        if (employeeTaxResidence && updatedTaxResidence) {
          cache.writeFragment({
            id: cache.identify(employeeTaxResidence),
            fragment: EmployeeBlocksEmployeeTaxResidenceFragmentDoc,
            fragmentName: 'EmployeeBlocksEmployeeTaxResidence',
            data: {
              ...employeeTaxResidence,
              addressLine1: updatedTaxResidence.addressLine1,
              addressLine2: updatedTaxResidence.addressLine2,
              city: updatedTaxResidence.city,
              country: updatedTaxResidence.country,
              identifier: updatedTaxResidence.identifier,
              noResidentEndDate: updatedTaxResidence.noResidentEndDate,
              postalCode: updatedTaxResidence.postalCode,
              state: updatedTaxResidence.state,
            },
          })
        }
      },
    }
  )

  const scrollToRow = (type: Props['scrollTo']): boolean => scrollTo === type

  const scrollToAddress =
    scrollTo && isScrollAddressRowType(scrollTo) ? ADDRESS_ROW_TYPES[scrollTo] : null

  const onSubmit = async () => {
    const residentEndDateValue = extraInformationForm.get('no_resident_end_date').value
    const residentEndDate = residentEndDateValue
      ? stringToDate(residentEndDateValue, 'ui').toISOString()
      : undefined
    const result = await upsertTaxResidence({
      variables: {
        addressLine1: extraInformationForm.get('address_line_1').value ?? '',
        addressLine2: extraInformationForm.get('address_line_2').value ?? '',
        city: extraInformationForm.get('city').value ?? '',
        country: extraInformationForm.get('country').value ?? '',
        employeeId: employeeId,
        identifier: extraInformationForm.get('identifier').value ?? '',
        noResidentEndDate: residentEndDate ?? '',
        postalCode: extraInformationForm.get('postal_code').value ?? '',
        state: extraInformationForm.get('state').value ?? '',
      },
    })

    const handled = handleResult(result, (errors) => {
      if (errors) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('profile.employee.tax_residence.update_tax_residence_data_error'),
        })
      }
    })

    if (handled.success) {
      extraInformationForm.cleanAll()
      app.setFlash({
        type: 'correct',
        message: i18n.t('profile.employee.tax_residence.update_tax_residence_data'),
      })
    }

    return handled
  }

  return (
    <InlineTooltip>
      <FormComponent>
        <FormRow>
          <AddressInput
            scrollTo={scrollToAddress}
            form={extraInformationForm}
            readonly={readonly}
          />
        </FormRow>
        <FormRow scrollToRow={scrollToRow('identifier')}>
          <Text
            label={i18n.t('employee_form.identifier')}
            field={extraInformationForm.get('identifier')}
            readonly={readonly}
          />
        </FormRow>
        <FormRow scrollToRow={scrollToRow('no_resident_end_date')}>
          <DayPicker
            label={i18n.t('employee_form.tax_residence.no_resident_end_date')}
            field={extraInformationForm.get('no_resident_end_date')}
            readonly={readonly}
          />
        </FormRow>
        {extraInformationForm.isDirty && (
          <FormRow footer>
            <Button onClick={onSubmit}>{i18n.t('common.submit')}</Button>
          </FormRow>
        )}
      </FormComponent>
    </InlineTooltip>
  )
}

export default observer(ExtraInformation)
